import React, { useEffect, useState } from 'react';
import { MailPlus, Trash2, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { OnboardingAPI } from '../../api';

const ago = (ts) => {
  if (!ts) return '—';
  const h = Math.floor((Date.now() - new Date(ts).getTime()) / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const UnfinishedAgreements = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => OnboardingAPI.drafts().then(setItems).catch(() => { setItems([]); toast.error('Failed to load unfinished agreements'); });
  useEffect(() => { load(); }, []);

  const remind = async (id) => {
    setBusy(id);
    try {
      const res = await OnboardingAPI.remindDraft(id);
      setItems((prev) => prev.map((d) => (d.id === id ? { ...d, reminders_sent: res.reminders_sent, last_reminder_at: new Date().toISOString() } : d)));
      toast.success('Reminder sent');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reminder'); } finally { setBusy(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this unfinished entry? No more reminders will be sent.')) return;
    try { await OnboardingAPI.removeDraft(id); setItems((prev) => prev.filter((d) => d.id !== id)); } catch (e) { toast.error('Failed to remove'); }
  };

  if (items === null) return <div className="text-[13px] text-[#6F76A7]">Loading…</div>;

  return (
    <div data-testid="unfinished-agreements">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-[13px] text-[#6F76A7]">Signers who started but never submitted. Automatic reminders go out after 24h and 72h.</p>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-[13px] text-[#0A1551] hover:underline shrink-0" data-testid="unfinished-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-[#E3E5F5] rounded-xl p-8 text-center" data-testid="unfinished-empty">
          <Clock className="w-8 h-8 mx-auto text-[#B0B4D0] mb-2" />
          <p className="text-[14px] font-semibold text-[#0A1551]">Nothing pending</p>
          <p className="text-[13px] text-[#6F76A7] mt-1">Everyone who started the agreement has finished it.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="bg-white border border-[#E3E5F5] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`unfinished-row-${d.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-[#0A1551] truncate">{[d.first_name, d.last_name].filter(Boolean).join(' ') || 'Unnamed signer'}</div>
                <div className="text-[13px] text-[#6F76A7] truncate">{d.email}{d.phone ? ` · ${d.phone}` : ''}</div>
                <div className="text-[12px] text-[#9599C0] mt-0.5">Started {ago(d.created_at)} · {d.reminders_sent || 0} reminder{(d.reminders_sent || 0) === 1 ? '' : 's'} sent{d.last_reminder_at ? ` · last ${ago(d.last_reminder_at)}` : ''}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => remind(d.id)} disabled={busy === d.id} data-testid={`unfinished-remind-${d.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[13px] font-semibold disabled:opacity-60">
                  <MailPlus className="w-3.5 h-3.5" /> {busy === d.id ? 'Sending…' : 'Send reminder'}
                </button>
                <button onClick={() => remove(d.id)} data-testid={`unfinished-delete-${d.id}`} className="p-2 rounded-md border border-[#F5C2D3] text-[#E5387B] hover:bg-[#FEF2F2]" aria-label="Remove entry">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnfinishedAgreements;
